/**
 * Centralized Error Handling
 *
 * Provides error classification, user-friendly messages, Sentry reporting,
 * and a single `handleError()` function for consistent error handling app-wide.
 *
 * Usage in components:
 *   import { handleError, getUserMessage } from '@/lib/errors';
 *
 *   catch (err) {
 *     handleError(err, { context: 'LoginScreen.handleEmailAuth' });
 *     showError('Sign In Failed', getUserMessage(err));
 *   }
 *
 * Usage with the useErrorHandler hook (inside components with AlertProvider):
 *   const onError = useErrorHandler();
 *   catch (err) {
 *     onError(err, { title: 'Sign In Failed', context: 'login' });
 *   }
 */

import * as Sentry from '@sentry/react-native';

// ---------------------------------------------------------------------------
// Error Categories
// ---------------------------------------------------------------------------

export type ErrorCategory =
  | 'network'      // No internet, DNS failure, fetch error
  | 'timeout'      // Request or operation timed out
  | 'auth'         // Invalid credentials, expired session, unauthorized
  | 'validation'   // Client-side validation or bad input
  | 'not_found'    // Resource not found (404)
  | 'rate_limit'   // Too many requests (429)
  | 'server'       // Server-side error (5xx)
  | 'cancelled'    // User cancelled the operation
  | 'unknown';     // Anything else

// ---------------------------------------------------------------------------
// classifyError — determine what kind of error this is
// ---------------------------------------------------------------------------

export function classifyError(error: unknown): ErrorCategory {
  if (error == null) return 'unknown';

  // Cancelled / aborted (DOMException is web-only, so check safely)
  if (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
  if (typeof error === 'object' && error !== null && 'name' in error && (error as Record<string, unknown>).name === 'AbortError') return 'cancelled';

  const message = getErrorString(error).toLowerCase();

  // User-cancelled flows
  if (message.includes('cancel') || message.includes('dismiss') || message.includes('user denied')) {
    return 'cancelled';
  }

  // Network errors
  if (
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('dns') ||
    message.includes('econnrefused') ||
    message.includes('enotfound') ||
    message.includes('no internet') ||
    message.includes('offline') ||
    message.includes('failed to fetch') ||
    message.includes('network request failed')
  ) {
    return 'network';
  }

  // Timeout
  if (message.includes('timeout') || message.includes('timed out') || message.includes('deadline')) {
    return 'timeout';
  }

  // Auth errors
  if (
    message.includes('invalid login') ||
    message.includes('invalid credentials') ||
    message.includes('invalid password') ||
    message.includes('wrong password') ||
    message.includes('unauthorized') ||
    message.includes('unauthenticated') ||
    message.includes('session expired') ||
    message.includes('jwt expired') ||
    message.includes('refresh_token') ||
    message.includes('email not confirmed')
  ) {
    return 'auth';
  }

  // Validation
  if (
    message.includes('validation') ||
    message.includes('invalid email') ||
    message.includes('too short') ||
    message.includes('too long') ||
    message.includes('required') ||
    message.includes('already registered') ||
    message.includes('already exists')
  ) {
    return 'validation';
  }

  // HTTP status code patterns (from Supabase or fetch errors)
  if (message.includes('404') || message.includes('not found')) return 'not_found';
  if (message.includes('429') || message.includes('rate limit') || message.includes('too many')) return 'rate_limit';
  if (message.includes('500') || message.includes('502') || message.includes('503') || message.includes('internal server')) return 'server';

  // Check status property
  if (typeof error === 'object') {
    const errObj = error as Record<string, unknown>;
    const status = (errObj.status as number | undefined) ?? (errObj.statusCode as number | undefined);
    if (typeof status === 'number') {
      if (status === 401 || status === 403) return 'auth';
      if (status === 404) return 'not_found';
      if (status === 429) return 'rate_limit';
      if (status >= 500) return 'server';
    }
  }

  return 'unknown';
}

// ---------------------------------------------------------------------------
// getUserMessage — human-friendly message for each category
// ---------------------------------------------------------------------------

const USER_MESSAGES: Record<ErrorCategory, string> = {
  network: 'No internet connection. Please check your network and try again.',
  timeout: 'The request took too long. Please try again.',
  auth: 'Authentication failed. Please check your credentials or sign in again.',
  validation: 'Please check your input and try again.',
  not_found: 'The requested resource could not be found.',
  rate_limit: 'Too many requests. Please wait a moment and try again.',
  server: 'Something went wrong on our end. Please try again later.',
  cancelled: '', // Cancelled operations should not show a message
  unknown: 'An unexpected error occurred. Please try again.',
};

/**
 * Get a user-friendly error message.
 *
 * If the error contains a sufficiently descriptive Supabase/API message,
 * that is returned instead of the generic one.
 */
export function getUserMessage(error: unknown): string {
  const category = classifyError(error);

  // Cancelled operations → empty string (caller should not show any UI)
  if (category === 'cancelled') return '';

  // For auth and validation, the original message is often useful
  if (category === 'auth' || category === 'validation') {
    const original = getErrorString(error);
    // Only use original if it looks like a real message (not a stack trace or object dump)
    if (original.length > 5 && original.length < 200 && !original.includes('\n')) {
      return original;
    }
  }

  // Postgres raises `objectionable_content` (P0001) from the messages/reviews
  // filter trigger (20260916120000_ugc_block_report_filter.sql). None of the
  // classifyError keyword buckets above recognise it, so without this it falls
  // through to the generic "unexpected error" copy — technically true, but it
  // hides the one thing the user actually needs to know: which words to change.
  if (typeof error === 'object' && error !== null && (error as { message?: unknown }).message === 'objectionable_content') {
    return "This can't be sent because it contains language that isn't allowed on Inverio.";
  }

  return USER_MESSAGES[category];
}

// ---------------------------------------------------------------------------
// reportError — log + send to Sentry
// ---------------------------------------------------------------------------

export interface ErrorContext {
  /** Where in the app the error happened, e.g. 'LoginScreen.handleEmailAuth' */
  context?: string;
  /** Extra data to attach to the Sentry event */
  extra?: Record<string, unknown>;
  /** Severity level: 'fatal' crashes the app, 'error' is default, 'warning' is non-critical */
  level?: 'fatal' | 'error' | 'warning' | 'info';
  /** If true, skip Sentry reporting (e.g. for expected errors) */
  silent?: boolean;
}

export function reportError(error: unknown, ctx: ErrorContext = {}): void {
  const category = classifyError(error);

  // Never report cancelled operations
  if (category === 'cancelled') return;

  const level = ctx.level ?? (category === 'server' || category === 'unknown' ? 'error' : 'warning');

  // Always log to console in dev
  const tag = ctx.context ? `[${ctx.context}]` : '[error]';
  if (level === 'error' || level === 'fatal') {
    console.error(tag, error);
  } else {
    console.warn(tag, error);
  }

  // Report to Sentry unless silent or a non-critical known category
  if (!ctx.silent && category !== 'network' && category !== 'timeout' && category !== 'rate_limit') {
    const wasError = error instanceof Error;
    const err = toError(error);

    Sentry.withScope((scope) => {
      scope.setLevel(level);
      scope.setTag('error_category', category);
      if (ctx.context) scope.setTag('error_context', ctx.context);

      // Postgres/PostgREST diagnostics, promoted to tags so they are searchable
      // and so `error_code` alone identifies a class of failure (e.g. 42501).
      const diag = errorDiagnostics(error);
      if (diag.code) scope.setTag('error_code', diag.code);
      if (ctx.extra || diag.details || diag.hint) {
        scope.setExtras({
          ...(diag.details ? { error_details: diag.details } : {}),
          ...(diag.hint ? { error_hint: diag.hint } : {}),
          ...ctx.extra,
        });
      }

      // A value we had to normalise carries NO genuine stack, so Sentry would
      // synthesise one from this very function and title the issue after the
      // reporting wrapper (`withScope`, `handleError`, …) — splitting one bug
      // across many issues. Group those by what they are instead. A real Error
      // keeps Sentry's default grouping: its stack is more precise than this.
      if (!wasError) {
        scope.setFingerprint([
          ctx.context ?? 'unknown',
          err.name,
          diag.code ?? fingerprintKey(err.message),
        ]);
      }

      Sentry.captureException(err);
    });

    // Mark the ORIGINAL value, not the normalised copy: the global React Query
    // net receives the value as thrown, and that is what it checks.
    markReported(error);
  }
}

/**
 * Marks a value that `reportError` has already sent to Sentry.
 *
 * `Symbol.for` rather than a fresh symbol so the mark survives two copies of
 * this module (web and native bundles resolve it independently).
 */
const REPORTED = Symbol.for('inverio.errors.reported');

function markReported(value: unknown): void {
  if (typeof value !== 'object' || value === null) return;
  try {
    Object.defineProperty(value, REPORTED, {
      value: true,
      enumerable: false,
      configurable: true,
      writable: true,
    });
  } catch {
    // Frozen or sealed. Marking is an optimisation, never a requirement —
    // failing to mark costs a duplicate report, and throwing here would turn
    // the error path itself into a second error.
  }
}

/**
 * Has this exact value already been reported to Sentry?
 *
 * For the GLOBAL React Query mutation handler, which is a safety net rather
 * than a reporter: a mutation whose screen already caught and reported the
 * failure — with a real context like `StartupDetail.handleRequestAccess` — must
 * not be reported a second time as `ReactQuery.mutation`. That doubling is why
 * one bug occupied four Sentry issues (INVERIO-APP-W/-V, -S/-T, -P/-Q, -X/-Y all
 * arrived as pairs: one handler-named, one `ReactQuery.mutation`).
 *
 * NOT consulted by `reportError` itself. An explicit report is always honoured;
 * only the net defers. A primitive (a thrown string) cannot carry the mark and
 * always reads false — a known gap, and better than crashing inside a failure.
 */
export function wasReported(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as Record<symbol, unknown>)[REPORTED] === true
  );
}

/**
 * Normalize any thrown/rejected value into a real `Error`.
 *
 * Exists because a Supabase `PostgrestError` — the single most common failure
 * value in this app — is a PLAIN OBJECT. Capturing it as a message instead of an
 * exception is what produced production issues titled `withScope` and
 * `generateMyCode`. One capture path, always an exception.
 */
export function toError(value: unknown): Error {
  if (value instanceof Error) return value;

  const message = getErrorString(value);
  const err = new Error(message, { cause: value });

  // Prefer the value's own name (`FunctionsHttpError`, `AuthApiError`, …); fall
  // back to PostgrestError for the `{ message, code, details, hint }` shape,
  // since that name is what makes the Sentry issue title readable.
  const obj = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
  if (obj && typeof obj.name === 'string' && obj.name) {
    err.name = obj.name;
  } else if (obj && typeof obj.code === 'string') {
    err.name = 'PostgrestError';
  }

  return err;
}

/** Postgres/PostgREST diagnostics, when the value carries them. */
function errorDiagnostics(value: unknown): { code?: string; details?: string; hint?: string } {
  if (typeof value !== 'object' || value === null) return {};
  const o = value as Record<string, unknown>;
  return {
    code: typeof o.code === 'string' && o.code ? o.code : undefined,
    details: typeof o.details === 'string' && o.details ? o.details : undefined,
    hint: typeof o.hint === 'string' && o.hint ? o.hint : undefined,
  };
}

/**
 * Collapse the volatile parts of a message so one bug is one issue.
 * "startup <uuid> not found" must not become a new Sentry issue per startup.
 */
function fingerprintKey(message: string): string {
  return message
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '<id>')
    .replace(/\b\d{3,}\b/g, '<n>')
    .trim();
}

/**
 * Tag (or clear) the Sentry user so production errors are attributable to a
 * user id — pass null on sign-out. No-op when Sentry isn't initialized
 * (dev/mock). Only the auth-user id leaves the device; the `beforeSend`
 * scrubber in `src/lib/sentry.ts` deliberately preserves `user.id` (an opaque
 * uuid, no PII) for correlation. Wired at the single onAuthStateChange point.
 */
export function setSentryUser(userId: string | null): void {
  Sentry.setUser(userId ? { id: userId } : null);
}

// ---------------------------------------------------------------------------
// handleError — single call for catch blocks (log + report + return message)
// ---------------------------------------------------------------------------

export interface HandleErrorResult {
  category: ErrorCategory;
  message: string;
  isCancelled: boolean;
}

/**
 * One-call error handler for catch blocks.
 *
 * Returns the category and user message so callers can decide whether to show UI.
 *
 * ```ts
 * catch (err) {
 *   const { message, isCancelled } = handleError(err, { context: 'fetchProfile' });
 *   if (!isCancelled) showError('Error', message);
 * }
 * ```
 */
export function handleError(error: unknown, ctx: ErrorContext = {}): HandleErrorResult {
  const category = classifyError(error);
  const message = getUserMessage(error);

  reportError(error, ctx);

  return { category, message, isCancelled: category === 'cancelled' };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Safely extract a string from any error value */
function getErrorString(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error !== null) {
    // Supabase errors often have .message or .error_description
    const obj = error as Record<string, unknown>;
    if (typeof obj.message === 'string') return obj.message;
    if (typeof obj.error_description === 'string') return obj.error_description;
    if (typeof obj.error === 'string') return obj.error;
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
  return String(error);
}

/**
 * Type guard: is this a network-type error that the user can retry?
 */
export function isRetryable(error: unknown): boolean {
  const cat = classifyError(error);
  return cat === 'network' || cat === 'timeout' || cat === 'server' || cat === 'rate_limit';
}
